import { Currency } from '@kodiak-finance/sdk-core'
import TickLensJson from '@kodiak-finance/v3-periphery/artifacts/contracts/lens/TickLens.sol/TickLens.json'
import { FeeAmount, nearestUsableTick, Pool, TICK_SPACINGS, TickMath, tickToPrice } from '@kodiak-finance/v3-sdk'
import { computePoolAddress } from '@kodiak-finance/v3-sdk'
import { DEFAULT_WAGMI_REFETCH_POLICY } from 'config/RefetchPolicies'
import { TICK_LENS_ADDRESSES, V3_FACTORY_ADDRESSES } from 'constants/addresses'
import { ZERO_ADDRESS } from 'constants/misc'
import { useAllV3Ticks as useAllV3TicksQuery, useV3PoolsByAddresses } from 'hooks/subgraph/v3'
import { useConnection } from 'hooks/useConnection'
import JSBI from 'jsbi'
import { useEffect, useMemo, useState } from 'react'
import computeSurroundingTicks from 'utils/computeSurroundingTicks'
import type { Abi } from 'viem'
import { Address } from 'viem'

import { PoolState, usePool } from './usePools'
import { useReadContractMultiple } from './useReadContractMultiple'

const PRICE_FIXED_DIGITS = 8

export interface TickData {
  tick: number
  liquidityNet: JSBI
  liquidityGross: JSBI
}

// Tick with fields parsed to JSBIs, and active liquidity computed.
export interface TickProcessed {
  tick: number
  liquidityActive: JSBI
  liquidityNet: JSBI
  liquidityGross: JSBI
  price0: string
}

const getActiveTick = (tickCurrent: number | undefined, feeAmount: FeeAmount | undefined) => {
  const ret =
    tickCurrent && feeAmount ? Math.floor(tickCurrent / TICK_SPACINGS[feeAmount]) * TICK_SPACINGS[feeAmount] : undefined
  if (ret && ret < TickMath.MIN_TICK) {
    return TickMath.MIN_TICK
  } else if (ret && ret > TickMath.MAX_TICK) {
    return ret
  }
  return ret
}

const bitmapIndex = (tick: number, tickSpacing: number) => {
  return Math.floor(tick / tickSpacing / 256)
}

function useTicksFromTickLens(
  currencyA: Currency | undefined,
  currencyB: Currency | undefined,
  feeAmount: FeeAmount | undefined,
  numSurroundingTicks: number | undefined = 125
) {
  const [tickDataLatestSynced, setTickDataLatestSynced] = useState<TickData[]>([])

  const [poolState, pool] = usePool(currencyA, currencyB, feeAmount)

  const tickSpacing = feeAmount && TICK_SPACINGS[feeAmount]

  // Find nearest valid tick for pool in case tick is not initialized.
  const activeTick = pool?.tickCurrent && tickSpacing ? nearestUsableTick(pool?.tickCurrent, tickSpacing) : undefined

  const { chainId } = useConnection()

  const poolAddress =
    currencyA && currencyB && feeAmount && poolState === PoolState.EXISTS
      ? Pool.getAddress(
          currencyA?.wrapped,
          currencyB?.wrapped,
          feeAmount,
          undefined,
          chainId ? V3_FACTORY_ADDRESSES[chainId] : undefined
        )
      : undefined

  // it is also possible to grab all tick data but it is extremely slow
  // bitmapIndex(nearestUsableTick(TickMath.MIN_TICK, tickSpacing), tickSpacing)
  const minIndex = useMemo(
    () =>
      tickSpacing && activeTick ? bitmapIndex(activeTick - numSurroundingTicks * tickSpacing, tickSpacing) : undefined,
    [tickSpacing, activeTick, numSurroundingTicks]
  )

  const maxIndex = useMemo(
    () =>
      tickSpacing && activeTick ? bitmapIndex(activeTick + numSurroundingTicks * tickSpacing, tickSpacing) : undefined,
    [tickSpacing, activeTick, numSurroundingTicks]
  )

  const tickLensAddress = TICK_LENS_ADDRESSES[chainId]

  const contracts = useMemo(() => {
    if (!maxIndex || !minIndex || !poolAddress || poolAddress === ZERO_ADDRESS || !tickLensAddress) {
      return []
    }

    return new Array(maxIndex - minIndex + 1)
      .fill(0)
      .map((_, i) => i + minIndex)
      .map((wordIndex) => ({
        address: tickLensAddress,
        abi: TickLensJson.abi as Abi,
        functionName: 'getPopulatedTicksInWord' as const,
        args: [poolAddress as Address, wordIndex] as const,
      }))
  }, [minIndex, maxIndex, poolAddress, tickLensAddress])

  const {
    data,
    isLoading,
    isFetching: IsSyncing,
    isError: wagmiIsError,
    error,
  } = useReadContractMultiple({
    contracts,
    query: {
      enabled: contracts.length > 0,
    },
  })

  const isValid = useMemo(() => !wagmiIsError && data && data.length > 0, [wagmiIsError, data])
  const isError = wagmiIsError

  const tickData: TickData[] = useMemo(
    () =>
      data
        ?.map((result) => {
          if (result.status === 'success' && result.result) {
            return (result.result as { populatedTicks: TickData[] }).populatedTicks
          }
          return []
        })
        .reduce(
          (accumulator, current) => [
            ...accumulator,
            ...(current?.map((tickData: TickData) => {
              return {
                tick: tickData.tick,
                liquidityNet: JSBI.BigInt(tickData.liquidityNet),
                liquidityGross: JSBI.BigInt(tickData.liquidityGross),
              }
            }) ?? []),
          ],
          []
        ) ?? [],
    [data]
  )

  // reset on input change
  useEffect(() => {
    setTickDataLatestSynced([])
  }, [currencyA, currencyB, feeAmount, numSurroundingTicks])

  // return the latest synced tickData even if we are still loading the newest data
  useEffect(() => {
    if (!IsSyncing && !isLoading && !isError && isValid) {
      setTickDataLatestSynced(tickData.sort((a, b) => a.tick - b.tick))
    }
  }, [isLoading, IsSyncing, isError, isValid, tickData])

  return useMemo(
    () => ({ isLoading, IsSyncing, isError, isValid, tickData: tickDataLatestSynced, error }),
    [isLoading, IsSyncing, isError, isValid, tickDataLatestSynced, error]
  )
}

function useTicksFromSubgraph(
  currencyA: Currency | undefined,
  currencyB: Currency | undefined,
  feeAmount: FeeAmount | undefined
) {
  const { chainId } = useConnection()
  const poolAddress =
    currencyA && currencyB && feeAmount
      ? Pool.getAddress(
          currencyA?.wrapped,
          currencyB?.wrapped,
          feeAmount,
          undefined,
          chainId ? V3_FACTORY_ADDRESSES[chainId] : undefined
        )
      : undefined

  return useAllV3TicksQuery({ poolAddress: poolAddress?.toLowerCase() ?? '', skip: 0 }, DEFAULT_WAGMI_REFETCH_POLICY)
}

// Fetches all ticks for a given pool
function useAllV3Ticks(
  currencyA: Currency | undefined,
  currencyB: Currency | undefined,
  feeAmount: FeeAmount | undefined,
  numSurroundingTicks?: number
): {
  isLoading: boolean
  isUninitialized: boolean
  isError: boolean
  error: unknown
  ticks: TickData[] | undefined
} {
  const useSubgraph = true
  // const useSubgraph = false

  const tickLensTickData = useTicksFromTickLens(
    !useSubgraph ? currencyA : undefined,
    currencyB,
    feeAmount,
    numSurroundingTicks
  )
  const subgraphTickData = useTicksFromSubgraph(useSubgraph ? currencyA : undefined, currencyB, feeAmount)

  const isLoading = useSubgraph ? subgraphTickData.isLoading : tickLensTickData.isLoading
  const isError = useSubgraph ? subgraphTickData.isError : tickLensTickData.isError
  const error = useSubgraph ? subgraphTickData.error : tickLensTickData.error
  const ticks = useSubgraph ? subgraphTickData.data?.ticks : tickLensTickData.tickData
  const isUninitialized = useSubgraph ? !ticks && !isLoading && !isError : false

  return {
    isLoading,
    isUninitialized,
    isError,
    error,
    ticks,
  }
}

export function usePoolActiveLiquidity(
  currencyA: Currency | undefined,
  currencyB: Currency | undefined,
  feeAmount: FeeAmount | undefined,
  numSurroundingTicks?: number
): {
  isLoading: boolean
  isUninitialized: boolean
  isError: boolean
  error: any
  activeTick: number | undefined
  data: TickProcessed[] | undefined
} {
  const { chainId } = useConnection()

  // Compute pool address
  const poolAddress = useMemo(() => {
    if (!currencyA || !currencyB || !feeAmount || !chainId) return undefined

    const tokenA = currencyA.wrapped
    const tokenB = currencyB.wrapped
    const factoryAddress = V3_FACTORY_ADDRESSES[chainId]

    if (!factoryAddress) return undefined

    return computePoolAddress({
      factoryAddress,
      tokenA: tokenB,
      tokenB: tokenA,
      fee: feeAmount,
    }).toLowerCase()
  }, [currencyA, currencyB, feeAmount, chainId])

  // Get pool data
  const {
    data: poolsData,
    isLoading: poolsLoading,
    isFetching: poolsFetching,
    isError: poolsError,
    error: poolsQueryError,
  } = useV3PoolsByAddresses(poolAddress ? [poolAddress] : [])

  // Extract the single pool from the response
  const pool = poolsData?.pools?.[0]

  const poolState = useMemo(() => {
    if (poolsLoading) return PoolState.LOADING
    if (!poolAddress) return PoolState.INVALID
    if (poolsError) return PoolState.INVALID
    if (!pool) return PoolState.NOT_EXISTS

    if (pool.tick == null || pool.sqrtPrice === null) return PoolState.NOT_EXISTS

    return PoolState.EXISTS
  }, [poolsLoading, poolsError, poolAddress, pool])

  // Find nearest valid tick for pool in case tick is not initialized.
  const activeTick = useMemo(() => {
    if (pool?.tick == null || feeAmount == null) return undefined
    return getActiveTick(parseInt(pool.tick), feeAmount)
  }, [pool?.tick, feeAmount])

  const { isLoading, isUninitialized, isError, error, ticks } = useAllV3Ticks(
    currencyA,
    currencyB,
    feeAmount,
    numSurroundingTicks
  )

  return useMemo(() => {
    const fetching = poolsFetching || isLoading || isUninitialized
    if (
      fetching ||
      !currencyA ||
      !currencyB ||
      activeTick === undefined ||
      poolState !== PoolState.EXISTS ||
      !ticks?.length
    ) {
      return {
        isLoading: poolsLoading || fetching || poolState === PoolState.LOADING,
        isUninitialized,
        isError: isError || poolsError,
        error: error || poolsQueryError,
        activeTick,
        data: undefined,
      }
    }

    const token0 = currencyA?.wrapped
    const token1 = currencyB?.wrapped

    // find where the active tick would be to partition the array
    // if the active tick is initialized, the pivot will be an element
    // if not, take the previous tick as pivot
    let pivot = ticks.findIndex(({ tick }) => tick > activeTick) - 1

    if (pivot < 0) {
      pivot = 0
    }

    const activeTickProcessed: TickProcessed = {
      liquidityActive: JSBI.BigInt(pool?.liquidity ?? 0),
      tick: activeTick,
      liquidityNet: Number(ticks[pivot].tick) === activeTick ? JSBI.BigInt(ticks[pivot].liquidityNet) : JSBI.BigInt(0),
      liquidityGross:
        Number(ticks[pivot].tick) === activeTick ? JSBI.BigInt(ticks[pivot].liquidityGross) : JSBI.BigInt(0),
      price0: tickToPrice(token0, token1, activeTick).toFixed(PRICE_FIXED_DIGITS),
    }

    const subsequentTicks = computeSurroundingTicks(token0, token1, activeTickProcessed, ticks, pivot, true)
    const previousTicks = computeSurroundingTicks(token0, token1, activeTickProcessed, ticks, pivot, false)
    const ticksProcessed = previousTicks.concat(activeTickProcessed).concat(subsequentTicks)

    return {
      isLoading,
      isUninitialized,
      isError: isError || poolsError,
      error: error || poolsQueryError,
      activeTick,
      data: ticksProcessed,
    }
  }, [
    currencyA,
    currencyB,
    activeTick,
    poolState,
    pool?.liquidity,
    ticks,
    isLoading,
    isUninitialized,
    isError,
    error,
    poolsError,
    poolsQueryError,
    poolsLoading,
    poolsFetching,
  ])
}
